import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/store/auth.store'
import { useCourses } from '@/features/courses/hooks/useCourses'
import type { Course } from '@/features/courses/types'

interface ChatHistorySidebarProps {
  readonly open: boolean
  readonly onClose: () => void
  readonly onNewChat: () => void
}

/** Clés i18n des tranches d'ancienneté, de la plus récente à la plus ancienne. */
const GROUP_KEYS = [
  'chat.groups.today',
  'chat.groups.yesterday',
  'chat.groups.last_7_days',
  'chat.groups.last_30_days',
  'chat.groups.older',
] as const

function groupCoursesByDate(courses: Course[]): Record<string, Course[]> {
  const now = new Date()
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterdayStart = new Date(todayStart.getTime() - 86400000)
  const last7Start = new Date(todayStart.getTime() - 7 * 86400000)
  const last30Start = new Date(todayStart.getTime() - 30 * 86400000)

  const [today, yesterday, last7, last30, older] = GROUP_KEYS
  const groups: Record<string, Course[]> = Object.fromEntries(
    GROUP_KEYS.map((key) => [key, [] as Course[]])
  )

  for (const course of courses) {
    const d = new Date(course.updatedAt)
    if (d >= todayStart) groups[today].push(course)
    else if (d >= yesterdayStart) groups[yesterday].push(course)
    else if (d >= last7Start) groups[last7].push(course)
    else if (d >= last30Start) groups[last30].push(course)
    else groups[older].push(course)
  }

  return groups
}

export function ChatHistorySidebar({ open, onClose, onNewChat }: ChatHistorySidebarProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  // Le paramètre s'appelle « courseId » sur une session et « id » sur la page
  // d'un cours : les deux doivent surligner la conversation courante.
  const { courseId, id } = useParams<{ courseId?: string; id?: string }>()
  const activeCourseId = courseId ?? id
  const user = useAuthStore((s) => s.user)
  const logout = useAuthStore((s) => s.logout)

  const { data: coursesRes, isLoading } = useCourses({ status: 'active', limit: 50 })
  const courses = (coursesRes?.data ?? []).slice().sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  )
  const groups = groupCoursesByDate(courses)

  function handleLogout() {
    logout()
    navigate('/auth/login')
  }

  function handleCourseClick(course: Course) {
    const storedSessionId = localStorage.getItem(`mindeia:session:${course.id}`)
    if (storedSessionId) {
      navigate(`/courses/${course.id}/sessions/${storedSessionId}`)
    } else {
      navigate(`/courses/${course.id}`)
    }
    onClose()
  }

  return (
    <aside
      className={[
        'fixed inset-y-0 left-0 z-30 flex flex-col w-72 max-w-[85vw]',
        'bg-white border-r border-outline-variant',
        'transition-transform duration-300 ease-in-out',
        'lg:static lg:translate-x-0 lg:shrink-0',
        open ? 'translate-x-0 shadow-2xl' : '-translate-x-full',
      ].join(' ')}
      aria-label={t('chat.history_title')}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-outline-variant shrink-0">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center shrink-0">
            <span className="text-white text-xs font-bold leading-none">M</span>
          </div>
          <span className="text-sm font-bold text-primary font-display">{t('app.name')}</span>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('chat.close_menu')}
            className="lg:hidden p-1.5 rounded-lg hover:bg-surface-low text-outline transition-colors"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5" aria-hidden="true">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
          </svg>
        </button>
      </div>

      {/* New chat button */}
      <div className="px-3 py-3 shrink-0">
        <button
          type="button"
          onClick={() => { onNewChat(); onClose() }}
          className="flex items-center gap-2.5 w-full px-3 py-2.5 rounded-xl bg-primary text-white text-sm font-semibold hover:opacity-90 transition-opacity"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 shrink-0" aria-hidden="true">
            <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
          </svg>
          {t('chat.new_conversation')}
        </button>
      </div>

      {/* Chat history list */}
      <nav className="flex-1 overflow-y-auto px-2 pb-2" aria-label={t('chat.history_nav')}>
        {isLoading && (
          <div className="space-y-1 px-2 pt-2">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-8 rounded-lg bg-surface-low animate-pulse" />
            ))}
          </div>
        )}

        {!isLoading && courses.length === 0 && (
          <div className="px-3 py-6 text-center">
            <p className="text-xs text-outline">{t('chat.empty_history')}</p>
          </div>
        )}

        {!isLoading && Object.entries(groups).map(([groupKey, items]) => {
          if (items.length === 0) return null
          return (
            <div key={groupKey} className="mb-3">
              <p className="px-3 py-1 text-[10px] font-semibold text-outline uppercase tracking-wider">
                {t(groupKey)}
              </p>
              <ul role="list">
                {items.map((course) => {
                  const isActive = course.id === activeCourseId
                  return (
                    <li key={course.id}>
                      <button
                        type="button"
                        onClick={() => handleCourseClick(course)}
                        className={[
                          'w-full text-left px-3 py-2 rounded-xl text-sm transition-colors',
                          'hover:bg-surface-low',
                          isActive
                            ? 'bg-primary/10 text-primary font-medium'
                            : 'text-on-surface-variant',
                        ].join(' ')}
                        title={course.title}
                      >
                        <span className="block truncate leading-snug">{course.title}</span>
                      </button>
                    </li>
                  )
                })}
              </ul>
            </div>
          )
        })}
      </nav>

      {/* User footer */}
      <div className="flex items-center gap-3 px-4 py-3 border-t border-outline-variant shrink-0">
        <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center shrink-0">
          <span className="text-xs font-bold text-white leading-none">
            {user?.first_name?.[0]?.toUpperCase() ?? 'U'}
          </span>
        </div>
        <p className="flex-1 text-sm font-medium text-on-surface truncate min-w-0">
          {user?.first_name} {user?.last_name}
        </p>
        <button
          type="button"
          onClick={handleLogout}
          aria-label={t('auth.logout')}
          title={t('auth.logout')}
          className="p-1.5 rounded-lg hover:bg-surface-low text-outline hover:text-on-surface transition-colors"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4" aria-hidden="true">
            <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" />
          </svg>
        </button>
      </div>
    </aside>
  )
}
